import { useCallback, useState } from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconClose from '@material-ui/icons/Close';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconSearch } from 'src/assets/icons/Search';
import { CUSTOM_ICON_BUTTON_SIZE, CustomIconButton } from 'src/components/CustomIconButton';
import CustomTextField from 'src/components/CustomTextField';

import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  onEnterPress: PropTypes.func,
  inputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  autoFocus: PropTypes.bool,
  onFocus: PropTypes.func,
  bluish: PropTypes.bool,
  hideIcon: PropTypes.bool,
  disabled: PropTypes.bool,
};

const defaultProps = {
  className: null,
  classes: null,
  value: undefined,
  placeholder: '',
  onEnterPress: null,
  inputRef: null,
  autoFocus: false,
  onFocus: null,
  bluish: false,
  hideIcon: false,
  disabled: false,
};

export const SearchField = ({
  className,
  classes,
  value,
  onChange,
  placeholder,
  onEnterPress,
  inputRef,
  autoFocus,
  onFocus,
  bluish,
  hideIcon,
  disabled,
}) => {
  const styles = useStyles({ classes });

  const [localValue, setLocalValue] = useState('');
  const alignedValue = value !== undefined ? value : localValue;

  const handleChange = useCallback(
    (event) => {
      setLocalValue(event.target.value);
      onChange(event.target.value);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
  }, [onChange]);

  return (
    <CustomTextField
      className={cx(styles.root, className, {
        [styles.bluish]: bluish,
      })}
      value={alignedValue}
      onChange={handleChange}
      onKeyPress={(event) => {
        if (event.key === 'Enter') {
          onEnterPress?.();
        }
      }}
      variant="outlined"
      placeholder={placeholder || 'Search'}
      disabled={disabled}
      InputProps={{
        classes: {
          root: styles.input,
          disabled: styles.disabled,
          notchedOutline: styles.outline,
        },
        startAdornment: !hideIcon && (
          <InputAdornment position="start">
            <IconSearch className={styles.iconSearch} />
          </InputAdornment>
        ),
        endAdornment: !hideIcon && alignedValue && (
          <CustomIconButton
            onClick={handleClear}
            disabled={disabled}
            size={CUSTOM_ICON_BUTTON_SIZE.medium}
            aria-label="Clear"
          >
            <IconClose />
          </CustomIconButton>
        ),
      }}
      InputLabelProps={{
        shrink: true,
      }}
      inputRef={inputRef}
      aria-label="SearchField"
      data-testid="searchField"
      autoFocus={autoFocus}
      onFocus={onFocus}
    />
  );
};

SearchField.propTypes = propTypes;
SearchField.defaultProps = defaultProps;
